const defaultCircleRadiusGenerator = (radius = null, hoverRadius = null) => {
  return [
    "case",
    ["boolean", ["feature-state", "hover"], false],
    hoverRadius ?? 9,
    ["boolean", ["get", "editing"], false],
    7,
    ["boolean", ["get", "pseudoHover"], false],
    9,
    ["boolean", ["feature-state", "selected"], false],
    7,
    radius ?? 5,
  ];
};

const relativeSizeCircleRadiusGenerator = (steps) => {
  return [
    "step",
    ["get", "count"],
    steps[0],
    10,
    steps[1],
    100,
    steps[2],
    1000,
    steps[3],
  ];
};

const defaultCircleColorGenerator = (fill = null) => {
  return [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    "#008000",
    fill || "#6a3d9a",
  ];
};

/** Turf Cutter district clusters (keep in sync with TurfCutterMapLegend.tsx) */
export const DISTRICT_STATUS_COLORS = {
  /** attempted === 0 */
  notStarted: "#94a3b8",
  /** attempted > 0 */
  started: "#7b3294",
  // Cut-progress palette — restore with cutRatio coloring in a later task:
  // notCut: "#d7301f",
  // partiallyCut: "#f2b705",
  // cut: "#2c7fb8",
};
/** Kept for restoring cut-% coloring later. */
export const DISTRICT_CUT_COMPLETE_RATIO = 0.98;
export const DISTRICT_FILL_OPACITY = 0.5;
export const DISTRICT_HOVER_STROKE = "#1f2937";
export const DISTRICT_SELECTED_GLOW = "#00e676";

const districtRadiusSteps = [10, 20, 30, 40];
const districtGlowPadding = 8;

const isSelected = ["boolean", ["feature-state", "selected"], false];
const isHovered = ["boolean", ["feature-state", "hover"], false];

const defaultCircleStrokeColorGenerator = (stroke = null) => {
  return [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    "#558C63",
    stroke || "#6a3d9a",
  ];
};

const defaultConfig = {
  checkGroup: false,
  radiusSystem: "default",
  radiusSteps: [10, 20, 30, 40],
};

const circleFillOpacityExpression = (config) => [
  "case",
  ["boolean", ["get", "disabled"], false],
  0.3,
  ["boolean", ["feature-state", "selected"], ["get", "editing"], false],
  0.8,
  ["boolean", ["feature-state", "hover"], false],
  0.6,
  ["boolean", ["get", "inGroup"], false],
  config.checkGroup ? 0.5 : 0.4,
  config.checkGroup ? 0.3 : 0.4,
];

const pointStyleGenerator = (config = defaultConfig) => {
  return {
    type: "circle",
    paint: {
      "circle-radius":
        config.radiusSystem === "relative"
          ? relativeSizeCircleRadiusGenerator(config.radiusSteps)
          : defaultCircleRadiusGenerator(config.radius, config.hoverRadius),
      "circle-color": defaultCircleColorGenerator(config.fill),
      "circle-opacity": circleFillOpacityExpression(config),
      "circle-stroke-color": defaultCircleStrokeColorGenerator(
        config.stroke || config.fill
      ),
      "circle-stroke-opacity": [
        "case",
        ["get", "disabled"],
        0.2,
        ["boolean", ["get", "inGroup"], false],
        1,
        config.checkGroup ? 0.8 : 1,
      ],
      "circle-stroke-width": config.strokeWidth ?? 1.5,
    },
  };
};

const districtStatusColorExpression = [
  "case",
  [">", ["get", "attemptedCount"], 0],
  DISTRICT_STATUS_COLORS.started,
  DISTRICT_STATUS_COLORS.notStarted,
];

/** Fill is a fixed status color at constant opacity; state is shown only by the stroke. */
export const districtStatusPointStyle = {
  id: "districtStatusPointLayer",
  type: "circle",
  paint: {
    "circle-radius": relativeSizeCircleRadiusGenerator(districtRadiusSteps),
    "circle-color": districtStatusColorExpression,
    "circle-opacity": DISTRICT_FILL_OPACITY,
    "circle-stroke-color": [
      "case",
      isSelected,
      DISTRICT_SELECTED_GLOW,
      DISTRICT_HOVER_STROKE,
    ],
    "circle-stroke-width": ["case", isSelected, 2, isHovered, 2, 0],
    "circle-stroke-opacity": ["case", isSelected, 1, isHovered, 1, 0],
  },
};

export const districtSelectionGlowStyle = {
  id: "districtSelectionGlowLayer",
  type: "circle",
  paint: {
    "circle-radius": relativeSizeCircleRadiusGenerator(
      districtRadiusSteps.map((r) => r + districtGlowPadding)
    ),
    "circle-color": DISTRICT_SELECTED_GLOW,
    "circle-blur": 0.6,
    "circle-opacity": ["case", isSelected, 0.9, 0],
  },
};

export const symbolStyleGenerator = () => {
  return {
    type: "symbol",
    layout: {
      "icon-image": ["get", "iconName"],
      "icon-allow-overlap": true,
      "icon-size": ["number", ["get", "iconSize"], 0.5],
    },
    paint: {
      "icon-color": ["get", "iconColor"],
      "icon-opacity": 0.6,
    },
  };
};

export const symbolStyleGeneratorNoColor = () => {
  return {
    type: "symbol",
    layout: {
      "icon-image": ["get", "iconName"],
      "icon-allow-overlap": true,
      "icon-size": ["number", ["get", "iconSize"], 0.5],
    },
    paint: {
      "icon-opacity": 0.6,
    },
  };
};

// IF YOU NEED MULTIPLE LAYERS WITH THE SAME STYLE, SET THE ID FOR THE STYLE IN THE BASE MAP OF THE COMPONENT, SEE TRACKERMAP2.JS FOR A WORKING EXAMPLE
export const defaultPointStyle = {
  id: "defaultPointLayer",
  ...pointStyleGenerator(),
};

export const onlyHoverPointStyle = {
  id: "onlyHoverPointStyle",
  ...pointStyleGenerator({
    ...defaultConfig,
    radius: 0,
    hoverRadius: 0,
    strokeWidth: 0,
  }),
};

export const groupedPointStyle = {
  id: "routePointLayer",
  ...pointStyleGenerator({
    ...defaultConfig,
    fill: ["get", "groupColor"],
    checkGroup: true,
  }),
};

export const groupedRelativePointStyle = {
  id: "groupedRelativePointLayer",
  ...pointStyleGenerator({
    ...defaultConfig,
    fill: ["get", "groupColor"],
    radiusSystem: "relative",
    checkGroup: true,
    radiusSteps: [4, 6, 8, 10],
  }),
};

export const mapReportsStyle = {
  id: "eventPointLayer",
  ...pointStyleGenerator({
    ...defaultConfig,
    fill: ["get", "groupColor"],
    radius: 4,
  }),
};


export const searchPointStyle = {
  id: "searchPointLayer",
  ...pointStyleGenerator({ ...defaultConfig, fill: "#f00" }),
};

export const labelStyleGenerator = (positioning = "default") => {
  return {
    type: "symbol",
    layout: {
      "text-field": ["get", "label"],
      "text-anchor": "top",
      "text-offset":
        positioning === "default"
          ? [0, 0.5]
          : [
              "step",
              ["get", "count"],
              // literal is a keyword required for when working with logical expressions that include literal arrays
              // in this weird little styling language
              ["literal", [0, 1]],
              10,
              ["literal", [0, 1.3]],
              100,
              ["literal", [0, 1.6]],
              1000,
              ["literal", [0, 2.1]],
            ],
      "text-size": ["case", ["get", "disabled"], 0, 12],
    },
    paint: {
      "text-halo-color": "white",
      "text-halo-width": 1,
    },
  };
};

export const labelStyle = {
  id: "labelStyle",
  ...labelStyleGenerator(),
};

export const relativeLabelStyle = {
  id: "relativeLabelStyle",
  ...labelStyleGenerator("relative"),
};

export const internalCountStyle = {
  id: "internalCountStyle",
  type: "symbol",
  layout: {
    "text-field": ["get", "count"],
    "text-anchor": "center",
    "text-size": 12,
  },
  paint: {
    "text-halo-color": "white",
    "text-halo-width": 1,
  },
};

export const gradientStyleGenerator = () => {
  return {
    type: "line",
    paint: {
      "line-gradient": [
        "interpolate",
        ["linear"],
        ["line-progress"],
        0,
        "#3d5941",
        0.15,
        "#778868",
        0.3,
        "#b5b991",
        0.45,
        "#d2b61d",
        0.6,
        "#edbb8a",
        0.75,
        "#de8a5a",
        1,
        "#ca562c",
      ],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        3,
        2,
      ],
    },
  };
};

export const lineStyleGenerator = () => {
  return {
    type: "line",
    paint: {
      "line-color": ["string", ["get", "lineColor"], "#9b4dca"],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        3,
        1,
      ],
    },
  };
};

export const polyStyleGenerator = (color = null) => {
  return {
    type: "fill",
    paint: {
      "fill-opacity": 0.3,
      // 'fill-color': color || '#9b4dca',

      "fill-color": ["string", ["get", "polyColor"], "#9b4dca"],
    },
  };
};


export const timelineStyle = {
  ...gradientStyleGenerator(),
  id: "timeline-line",
};

export const gradientLineStyle = {
  id: "gradientLineStyle",
  ...gradientStyleGenerator(),
};

export const defaultLineStyle = {
  id: "defaultLineStyle",
  ...lineStyleGenerator(),
};

export const polyStrokeStyle = {
  id: "polyStrokeStyle",
  ...lineStyleGenerator(),
};

export const defaultSymbolStyle = {
  id: "defaultSymbolLayer",
  ...symbolStyleGenerator(),
};

export const homeFilledSymbolStyle = {
  id: "homeFilledSymbolLayer",
  ...symbolStyleGeneratorNoColor(),
};

export const defaultPolyStyle = {
  id: "defaultPolyStyle",
  ...polyStyleGenerator(),
};
